import { useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Tooltip } from '@/components/ui/Tooltip';
import { useProvisionWorkspace, useTenantPlan } from '@/api/tenants';
import { toast } from '@/components/ui/Toaster';
import { DROPLET_SIZE_LABEL, DROPLET_SIZE_COST, type DropletSize } from '@/lib/droplet';
import { SIZE_ORDER, tierLabel } from '@/lib/tier';
import type { Tenant } from '@/data/types';
import { cn } from '@/lib/cn';

interface Props {
  open: boolean;
  onClose: () => void;
  tenant: Tenant;
}

function computeHostname(subdomain: string, environment: 'production' | 'staging' | 'development'): string {
  const s = subdomain.trim();
  if (!s) return '';
  if (environment === 'production') return `${s}.nousviz.app`;
  if (environment === 'staging') return `${s}.staging.nousviz.app`;
  return `${s}.dev.nousviz.app`;
}

export function ProvisionWorkspaceModal({ open, onClose, tenant }: Props) {
  const provision = useProvisionWorkspace();
  const [subdomain, setSubdomain] = useState(tenant.slug);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<'production' | 'staging' | 'development'>('staging');
  const [region, setRegion] = useState<'lon1' | 'fra1' | 'nyc3'>('lon1');
  const [dropletSize, setDropletSize] = useState<DropletSize>('s-2vcpu-4gb');

  const hostname = computeHostname(subdomain, environment);
  const validSubdomain = /^[a-z0-9][a-z0-9-]{1,40}[a-z0-9]$/.test(subdomain.trim());
  const canSubmit = validSubdomain && name.trim().length > 0 && !provision.isPending;

  // Sizes the customer's plan allows (backend enforces; this filters the
  // picker). Effective limits come from the plan registry via /plan; while
  // loading, everything is enabled — the backend still rejects.
  const plan = useTenantPlan(tenant.slug);
  const maxSize = plan.data?.limits.max_size ?? null;
  const planName = plan.data?.plans.find(p => p.slug === plan.data!.tier)?.name ?? tierLabel(tenant.tier);
  const sizeAllowed = (s: DropletSize) =>
    maxSize === null || SIZE_ORDER.indexOf(s) <= SIZE_ORDER.indexOf(maxSize);

  function reset() {
    setSubdomain(tenant.slug);
    setName('');
    setEnvironment('staging');
    setRegion('lon1');
    setDropletSize('s-2vcpu-4gb');
  }

  async function submit() {
    if (!canSubmit) return;
    try {
      const r = await provision.mutateAsync({
        tenantId: tenant.slug,
        subdomain: subdomain.trim(),
        name: name.trim(),
        environment,
        region,
        droplet_size: dropletSize,
      });
      toast.info(`Creating ${name}`, r.message, {
        to: `/tenants/${tenant.slug}/workspaces`,
        label: 'Watch progress',
      });
      reset();
      onClose();
    } catch (e: any) {
      toast.danger('Could not start provisioning', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!provision.isPending) { reset(); onClose(); } }}
      size="lg"
      title={`Create a new instance for ${tenant.name}`}
      description={
        <>
          Creates a new DigitalOcean droplet (in the <code className="text-xs font-mono bg-surface-2 px-1 rounded">NousViz</code> project),
          runs cloud-init to install NousViz, creates a Cloudflare A record for the
          assigned hostname, and polls{' '}
          <code className="text-xs font-mono bg-surface-2 px-1 rounded">/api/health</code> until ready.
          Takes ~10 minutes end-to-end — watch live progress on the workspace page.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={provision.isPending}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {provision.isPending ? (
              <><Loader2 size={13} className="animate-spin mr-1.5" /> Starting…</>
            ) : (
              `Create instance`
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Subdomain <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={subdomain}
            onChange={e => setSubdomain(e.target.value.toLowerCase())}
            placeholder="e.g. joe, acme, statsdrone"
            autoFocus
            disabled={provision.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
          />
          <div className="mt-1 text-2xs text-fg-subtle">
            Lowercase letters, digits, dashes. Becomes the instance's web address.
          </div>
          {!validSubdomain && subdomain.length > 0 && (
            <div className="mt-1 text-2xs text-[color:var(--danger)] inline-flex items-center gap-1">
              <AlertCircle size={11} /> Subdomain must start and end with a letter/digit
            </div>
          )}
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Display name <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Production, Staging, EU Production"
            disabled={provision.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Environment
            </label>
            <select
              value={environment}
              onChange={e => setEnvironment(e.target.value as any)}
              disabled={provision.isPending}
              className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            >
              <option value="production">Production</option>
              <option value="staging">Staging</option>
              <option value="development">Development</option>
            </select>
          </div>
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Region
            </label>
            <select
              value={region}
              onChange={e => setRegion(e.target.value as any)}
              disabled={provision.isPending}
              className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            >
              <option value="lon1">London (lon1)</option>
              <option value="fra1">Frankfurt (fra1)</option>
              <option value="nyc3">New York (nyc3)</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Droplet size
          </label>
          <Tooltip
            maxWidth={320}
            content={
              <div className="space-y-1 text-[11px]">
                <div className="opacity-70">DigitalOcean list prices:</div>
                {(Object.keys(DROPLET_SIZE_COST) as DropletSize[]).filter(k => k !== 'shared' && k !== 'byo').map(k => (
                  <div key={k} className="flex justify-between gap-3">
                    <span>{DROPLET_SIZE_LABEL[k]}</span>
                    <span className="font-mono">${DROPLET_SIZE_COST[k].toFixed(0)}/mo</span>
                  </div>
                ))}
              </div>
            }
          >
            <select
              value={dropletSize}
              onChange={e => setDropletSize(e.target.value as DropletSize)}
              disabled={provision.isPending}
              className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            >
              {(Object.keys(DROPLET_SIZE_LABEL) as DropletSize[]).filter(k => k !== 'shared' && k !== 'byo').map(k => (
                <option key={k} value={k} disabled={!sizeAllowed(k)}>
                  {DROPLET_SIZE_LABEL[k]} — ${DROPLET_SIZE_COST[k].toFixed(0)}/mo{!sizeAllowed(k) ? ' (needs plan upgrade)' : ''}
                </option>
              ))}
            </select>
          </Tooltip>
          {maxSize !== null && (
            <div className="mt-1 text-2xs text-fg-subtle">
              The {planName} plan allows servers up to {DROPLET_SIZE_LABEL[maxSize as DropletSize] ?? maxSize}.
            </div>
          )}
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Hostname
          </label>
          <div className={cn(
            'h-8 px-2.5 rounded-md bg-surface-2 border border-border text-sm font-mono inline-flex items-center w-full',
            !validSubdomain && 'text-fg-subtle',
          )}>
            {hostname || `<subdomain>.${environment === 'production' ? '' : (environment === 'staging' ? 'staging.' : 'dev.')}nousviz.app`}
          </div>
          <div className="mt-1 text-2xs text-fg-subtle">
            Cloudflare A record will be created automatically when the droplet has an IP.
          </div>
        </div>

        <div className="text-2xs text-fg-subtle border-t border-border pt-3 leading-relaxed">
          On submit: <strong>DO droplet created</strong> in the NousViz project · cloud-init installs NousViz via{' '}
          <code className="font-mono">scripts/setup.sh --server</code> · Cloudflare A record →{' '}
          <code className="font-mono">{hostname || '<hostname>'}</code> · Let's Encrypt cert issued, then switched
          to the platform wildcard ·{' '}
          <code className="font-mono">/api/health</code> polled until live (up to 20 min).
        </div>
      </div>
    </Modal>
  );
}
